import api from '@common/api';
import { FileKeyBucketInput, Partner } from '@services/appsync/types';
import { PartnerInfo } from 'src/apps/admin/partners/types';
import { createStore, useStore } from 'zustand';
import { AsyncResult } from './types';

interface PartnersModelProps {
  partners: Partner[];
}

interface PartnersModelActions {
  fetchPartners: () => Promise<AsyncResult<Partner[]>>;
  savePartner: (
    partner: PartnerInfo,
    tempPrimaryLogo?: FileKeyBucketInput,
    tempSecondaryLogo?: FileKeyBucketInput,
  ) => Promise<AsyncResult>;
}

export const PartnersModel = createStore<PartnersModelProps & PartnersModelActions>((set) => ({
  fetchPartners: async () => {
    try {
      const partners = await api.getPartners();
      set({ partners });
      return { data: partners, success: true };
    } catch {
      return { error: 'Unable to fetch partners', success: false };
    }
  },
  partners: [],
  savePartner: async (partner, tempPrimaryLogo, tempSecondaryLogo) => {
    try {
      let primary_logo = (tempPrimaryLogo ?? partner.style.primary_logo ?? null) as FileKeyBucketInput | null;
      if (!primary_logo?.bucket || !primary_logo?.key) {
        primary_logo = null;
      } else {
        primary_logo = { bucket: primary_logo.bucket, key: primary_logo.key };
      }
      let secondary_logo = (tempSecondaryLogo ?? partner.style.secondary_logo ?? null) as FileKeyBucketInput | null;
      if (!secondary_logo?.bucket || !secondary_logo?.key) {
        secondary_logo = null;
      } else {
        secondary_logo = { bucket: secondary_logo.bucket, key: secondary_logo.key };
      }
      const status = await api.savePartner({ ...partner, style: { ...partner.style, primary_logo, secondary_logo } });
      return { success: status };
    } catch (err) {
      return { success: false };
    }
  },
}));

export function usePartnersModel(): PartnersModelProps & PartnersModelActions;
export function usePartnersModel<T>(
  selector: (state: PartnersModelProps & PartnersModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function usePartnersModel<T>(
  selector?: (state: PartnersModelProps & PartnersModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(PartnersModel, selector!, equals);
}
